import React, {useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {Button, Detail, EMPHASIS, FlexRow, SubHeading, Text, TextField} from '@forecast-it/design-system';
import Util from '../../../../forecast-app/shared/util/util';
import AddCompanyToFeatureFlagCohort from '../../../../mutations/mission_control/add_company_to_feature_flag_cohort_mutation';
import CreateFeatureFlagCohort from '../../../../mutations/mission_control/create_feature_flag_cohort_mutation';
import DeleteFeatureFlagCohort from '../../../../mutations/mission_control/delete_feature_flag_cohort_mutation';
import RemoveCompanyFromFeatureFlagCohort from '../../../../mutations/mission_control/remove_company_from_feature_flag_cohort_mutation';
import RenameFeatureFlagCohort from '../../../../mutations/mission_control/rename_feature_flag_cohort_mutation';
import {
	FEATURE_FLAG_CARD_BORDER,
	featureFlagCardSurface,
	FeatureFlagSelectableRow,
	FeatureFlagSelectableRowLabel,
} from '../featureFlagSurface';
import {RolloutDirectory} from '../rollout/featureFlagRolloutDirectory';
import {CompanyMembershipTable} from './CompanyMembershipTable';
import {CompanyPickerBar} from './CompanyPickerBar';
import {DeleteCohortModal} from './DeleteCohortModal';
import {
	buildCompanyPickerOptions,
	buildMembershipRows,
	CohortView,
	MembershipIndex,
	parseCohortRejectionMessage,
} from './featureFlagLists';

const Layout = styled.div`
	display: flex;
	align-items: flex-start;
	gap: 20px;
`;

const Sidebar = styled.div`
	${featureFlagCardSurface}
	flex: none;
	width: 320px;
	display: flex;
	flex-direction: column;
	overflow: hidden;
`;

const SidebarHeader = styled.div`
	padding: 14px 16px;
	border-bottom: 1px solid ${FEATURE_FLAG_CARD_BORDER};
`;

const CohortList = styled.div`
	display: flex;
	flex-direction: column;
`;

const CreateRow = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	padding: 14px 16px;
	border-top: 1px solid ${FEATURE_FLAG_CARD_BORDER};
`;

const DetailPane = styled.div`
	${featureFlagCardSurface}
	flex: 1;
	min-width: 0;
`;

const DetailHeader = styled.div`
	display: flex;
	align-items: flex-end;
	justify-content: space-between;
	gap: 16px;
	padding: 16px 18px;
	border-bottom: 1px solid ${FEATURE_FLAG_CARD_BORDER};
`;

const NameFieldBlock = styled.div`
	flex: 1;
	min-width: 0;
	max-width: 360px;
`;

const EmptyState = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 6px;
	padding: 64px 20px;
`;

const PICKER_LABEL = 'Add company to cohort';

// Matches NAME_MAX_LENGTH in FeatureFlagListLogic.java, so an over-long name is prevented at the
// field rather than round-tripped to the backend just to be refused.
const NAME_MAX_LENGTH = 100;

function companyCountLabel(count: number): string {
	return count === 1 ? '1 company' : `${count} companies`;
}

/**
 * What the delete confirmation is about, captured at the moment Delete is clicked.
 *
 * Deliberately not derived live from `selectedCohort`: selection can be reassigned out from under the
 * operator while the dialog is open (reconciliation following a newly created cohort, or falling back
 * after the selected cohort itself disappears), and a confirmation dialog must never silently swap what
 * it is asking about. Everything the dialog shows and everything a confirm click sends comes from this
 * fixed snapshot, not from whatever happens to be selected at click time.
 */
interface DeleteTarget {
	cohortId: string;
	name: string;
	memberCount: number;
}

export interface CohortsTabProps {
	cohorts: readonly CohortView[];
	index: MembershipIndex;
	directory: RolloutDirectory;
}

export function CohortsTab({cohorts, index, directory}: CohortsTabProps): JSX.Element {
	const [selectedCohortId, setSelectedCohortId] = useState<string | null>(cohorts[0]?.cohortId ?? null);
	const [newCohortName, setNewCohortName] = useState('');
	// The backend's own refusal reason for the create field — a duplicate, blank, or over-long name —
	// shown inline rather than behind the generic error modal, since it is something the operator typed
	// and needs to see right next to where they typed it.
	const [createNameError, setCreateNameError] = useState<string | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

	// Every cohort id seen on the previous render, so a cohort id present now but absent before can be
	// told apart from one that was already on the list. Selection follows the former (a newly created
	// cohort) and falls back away from the latter only when it was the one selected (deleted out from
	// under the current selection, by this operator or another admin).
	const previousCohortIdsRef = useRef<Set<string>>(new Set(cohorts.map(cohort => cohort.cohortId)));

	useEffect(() => {
		const currentIds = new Set(cohorts.map(cohort => cohort.cohortId));
		const newlyCreated = cohorts.find(cohort => !previousCohortIdsRef.current.has(cohort.cohortId));
		if (newlyCreated) {
			setSelectedCohortId(newlyCreated.cohortId);
		} else if (selectedCohortId != null && !currentIds.has(selectedCohortId)) {
			setSelectedCohortId(cohorts[0]?.cohortId ?? null);
		}
		previousCohortIdsRef.current = currentIds;
		// Only the cohort list itself drives this reconciliation; re-running it on every manual
		// selection change would re-derive nothing new, since a manually picked cohort id is always
		// already a member of `currentIds`.

		// The cohort a confirmation is open for can disappear out from under it too (this operator's own
		// confirmed delete landing, or another admin deleting it independently). Rather than leave the
		// dialog open offering to delete something that no longer exists — or, worse, silently reassigning
		// itself to whatever selection falls back to — it closes. The confirmation was about one specific
		// cohort; once that cohort is gone, there is nothing left for it to confirm.
		setDeleteTarget(current => (current != null && !currentIds.has(current.cohortId) ? null : current));
	}, [cohorts]);

	const selectedCohort = cohorts.find(cohort => cohort.cohortId === selectedCohortId) ?? null;

	// Holds the typed value while editing; only committed to `RenameFeatureFlagCohort` on blur or
	// Enter, never per keystroke. Re-synced whenever the selection moves to a different cohort, so
	// switching cohorts always shows that cohort's own name rather than a draft left over from another.
	const [nameDraft, setNameDraft] = useState(selectedCohort?.name ?? '');
	// The backend's own refusal reason for the rename field, same treatment as `createNameError`.
	const [renameNameError, setRenameNameError] = useState<string | null>(null);
	useEffect(() => {
		setNameDraft(selectedCohort?.name ?? '');
		setRenameNameError(null);
	}, [selectedCohort?.cohortId]);

	const handleCreate = (): void => {
		const name = newCohortName.trim();
		if (name === '') {
			return;
		}
		setCreateNameError(null);
		Util.CommitMutation(
			CreateFeatureFlagCohort,
			{name},
			() => setNewCohortName(''),
			true,
			null,
			(transaction: unknown, onFailure: (transaction: unknown) => boolean) => {
				// A duplicate, blank, or over-long name arrives as a readable reason from the backend —
				// show it next to the field the operator typed into rather than falling through to the
				// generic error modal. Anything else (a genuine network or auth failure) is not ours to
				// handle, so it goes to the shared fallback exactly as the default handling would.
				const message = parseCohortRejectionMessage(String(transaction));
				if (message != null) {
					setCreateNameError(message);
					return;
				}
				onFailure(transaction);
			}
		);
	};

	const commitRename = (): void => {
		if (selectedCohort == null) {
			return;
		}
		// Compared trimmed against trimmed: the server also trims on write, so a draft that only differs
		// from the stored name by leading/trailing whitespace is not a change at all. Comparing the raw
		// draft here would re-fire this same rename on every subsequent blur, forever, once the server's
		// trimmed name stopped matching the untrimmed draft still sitting in state.
		const trimmedDraft = nameDraft.trim();
		if (trimmedDraft === selectedCohort.name) {
			return;
		}
		if (trimmedDraft === '') {
			setNameDraft(selectedCohort.name);
			return;
		}
		setRenameNameError(null);
		const cohortId = selectedCohort.cohortId;
		const originalName = selectedCohort.name;
		Util.CommitMutation(
			RenameFeatureFlagCohort,
			{cohortId, name: nameDraft},
			() => undefined,
			true,
			null,
			(transaction: unknown, onFailure: (transaction: unknown) => boolean) => {
				const message = parseCohortRejectionMessage(String(transaction));
				if (message != null) {
					setRenameNameError(message);
					return;
				}
				const retrying = onFailure(transaction);
				if (retrying) {
					return;
				}
				setNameDraft(originalName);
			}
		);
	};

	// TextField exposes no onKeyDown of its own; Enter-to-commit is wired on the wrapping block, since
	// the input's native keydown still bubbles up to it.
	const handleNameKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
		if (event.key === 'Enter') {
			event.preventDefault();
			commitRename();
		}
	};

	const handleConfirmDelete = (): void => {
		if (deleteTarget == null) {
			return;
		}
		Util.CommitMutation(DeleteFeatureFlagCohort, {cohortId: deleteTarget.cohortId}, () => undefined);
		setDeleteTarget(null);
	};

	const handleAddCompany = (companyId: string): void => {
		if (selectedCohort == null) {
			return;
		}
		Util.CommitMutation(AddCompanyToFeatureFlagCohort, {cohortId: selectedCohort.cohortId, companyId}, () => undefined);
	};

	const handleRemoveCompany = (companyId: string): void => {
		if (selectedCohort == null) {
			return;
		}
		Util.CommitMutation(
			RemoveCompanyFromFeatureFlagCohort,
			{cohortId: selectedCohort.cohortId, companyId},
			() => undefined
		);
	};

	return (
		<Layout>
			<Sidebar>
				<SidebarHeader>
					<SubHeading>Cohorts</SubHeading>
				</SidebarHeader>
				<CohortList>
					{cohorts.map(cohort => {
						const selected = cohort.cohortId === selectedCohortId;
						return (
							<FeatureFlagSelectableRow key={cohort.cohortId} $selected={selected}>
								<Button
									fullWidth
									emphasis={EMPHASIS.LOW}
									onPress={() => setSelectedCohortId(cohort.cohortId)}
									data-cy={`cohort-list-item-${cohort.cohortId}`}
								>
									<FlexRow gap="xs" justifyContent="space-between" alignItems="center">
										<FeatureFlagSelectableRowLabel $selected={selected}>
											<Text>{cohort.name}</Text>
										</FeatureFlagSelectableRowLabel>
										<Detail color="medium">{companyCountLabel(cohort.memberCompanyIds.length)}</Detail>
									</FlexRow>
								</Button>
							</FeatureFlagSelectableRow>
						);
					})}
				</CohortList>
				<CreateRow>
					<TextField
						label="New cohort"
						placeholder="New cohort name"
						value={newCohortName}
						onValueChange={value => {
							setNewCohortName(value);
							setCreateNameError(null);
						}}
						maxLength={NAME_MAX_LENGTH}
						errorText={createNameError ?? undefined}
						data-cy="cohort-new-name"
					/>
					<Button
						emphasis={EMPHASIS.HIGH}
						onPress={handleCreate}
						disabled={newCohortName.trim() === ''}
						data-cy="cohort-create"
					>
						Create cohort
					</Button>
				</CreateRow>
			</Sidebar>
			{selectedCohort == null ? (
				<DetailPane>
					<EmptyState>
						<Text>No cohort selected</Text>
						<Detail color="medium">
							Pick a cohort on the left, or create one to group companies for staged rollouts.
						</Detail>
					</EmptyState>
				</DetailPane>
			) : (
				<DetailPane>
					<DetailHeader>
						<NameFieldBlock onKeyDown={handleNameKeyDown}>
							<TextField
								label="Cohort name"
								value={nameDraft}
								onValueChange={value => {
									setNameDraft(value);
									setRenameNameError(null);
								}}
								onBlur={commitRename}
								maxLength={NAME_MAX_LENGTH}
								errorText={renameNameError ?? undefined}
								data-cy="cohort-name"
							/>
						</NameFieldBlock>
						<Button
							emphasis={EMPHASIS.LOW}
							onPress={() =>
								setDeleteTarget({
									cohortId: selectedCohort.cohortId,
									name: selectedCohort.name,
									memberCount: selectedCohort.memberCompanyIds.length,
								})
							}
							data-cy="cohort-delete"
						>
							Delete cohort
						</Button>
					</DetailHeader>
					<CompanyPickerBar
						label={PICKER_LABEL}
						options={buildCompanyPickerOptions(directory, new Set(selectedCohort.memberCompanyIds))}
						onAdd={handleAddCompany}
					/>
					<CompanyMembershipTable
						rows={buildMembershipRows(selectedCohort.memberCompanyIds, index, directory, {
							kind: 'cohort',
							name: selectedCohort.name,
						})}
						onRemove={handleRemoveCompany}
						emptyTitle="No companies in this cohort"
						emptyHint="Add companies above — an empty cohort adds nothing to a rollout."
					/>
				</DetailPane>
			)}
			{deleteTarget != null ? (
				<DeleteCohortModal
					cohortName={deleteTarget.name}
					memberCount={deleteTarget.memberCount}
					onConfirm={handleConfirmDelete}
					onClose={() => setDeleteTarget(null)}
				/>
			) : null}
		</Layout>
	);
}
